/**
 * `/demo` — persona picker for the Tarii demo.
 *
 * Per `docs/POC_SHOWCASE_SPEC.md` §2.1 + `engineering-poc-showcase.md`
 * step 6. The visitor lands here, sees one card per scenario (each with
 * a list of personas on that scenario's team), clicks a persona, and is
 * signed in as that account via the dev-shim Bearer header.
 *
 * Demo mode also auto-flips on for `*.pages.dev` / `*.fly.dev` hosts so
 * the persona/scenario switcher appears in the user pill without a
 * rebuild — see `lib/demo.ts`.
 */
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Layers, Sparkles, Users } from "lucide-react";
import { useMemo, useState } from "react";

import { apiFetch } from "../api/client";
import { Logo } from "../components/Logo";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  PageLead,
  Skeleton,
} from "../components/ui";
import { setDevAccountId } from "../lib/auth";
import { cn } from "../lib/cn";
import {
  DEFAULT_DEMO_SCENARIO,
  type DevScenario,
  getLastScenario,
  setActiveScenario,
  setDemoMode,
  setLastScenario,
} from "../lib/demo";
import { leaveDemoOnClick } from "../lib/demoSession";
import "./marketing/marketing-brand.css";

/**
 * Hand-written one-line "what this teaches" subtitle per scenario,
 * keyed by manifest `name`. Sourced from the JSON's `story` field but
 * tightened to a single sentence — the raw `story` is paragraph-length
 * (~450-510 chars) and would not anchor a card. The subtitle answers
 * "if I open this scenario, what will I learn?" — which is the gap
 * Dr. Mehta surfaced as F5 (doctor-persona walkthrough §9): a
 * first-time visitor lands on `/demo` and has no idea which card to
 * pick or what each one demonstrates.
 *
 * Per-card focal: scenario name (largest text); this subtitle is
 * supporting context (`text-fg-muted`).
 *
 * **All six are hand-writes (founder may override) — flagged in commit
 * body.** If a scenario is added without an entry here, the card
 * silently omits the subtitle line (degrades gracefully).
 */
const TEACHING_SUBTITLES: Record<string, string> = {
  clinical_first_run:
    "How a brand-new team walks the Setup flow before rostering their first dates.",
  clinical_baseline_balanced:
    "The calibration anchor — a calm roster on default config that every other scenario reads against.",
  clinical_tier_bottleneck:
    "How Tarii surfaces a thin senior bench and offers the lead two routes to close the gap.",
  ed_demand_weighted:
    "Demand-weighted scoring — peak cover honoured first while workload stays fair on the hours axis.",
  medicine_capacity_discovery:
    "Mode B (capacity discovery) — finding which members have spare time to redeploy.",
  cardiology_sustainability_history:
    "Three back-to-back published rosters light up the fairness-trend and worker trajectory panels.",
};

export function DemoPage() {
  const scenariosQuery = useQuery({
    queryKey: ["dev", "scenarios"],
    queryFn: () => apiFetch<DevScenario[]>("/api/dev/scenarios"),
  });

  // Auto-pick which scenario to highlight on this render. Read once
  // per mount — `useMemo` not `useEffect`, so the first paint already
  // shows the focal ring (no flicker).
  //   - Returning visitor: their `tarii.demo.last_scenario` hint.
  //   - First-time visitor (no hint): `DEFAULT_DEMO_SCENARIO`
  //     (`clinical_first_run` — the empty-workspace onboarding).
  // We resolve the *initial* hint from localStorage and keep it as
  // local state so a click can update the focal card without writing
  // to localStorage twice. The picker remains explicit — clicking any
  // card swaps the focal + writes localStorage, but no scenario is
  // auto-opened on mount; the visitor always confirms by clicking.
  const initialAutoPick = useMemo<string>(() => {
    const last = getLastScenario();
    return last ?? DEFAULT_DEMO_SCENARIO;
  }, []);
  const [highlighted, setHighlighted] = useState<string>(initialAutoPick);
  // Whether the highlight is still the auto-pick (i.e., the visitor
  // hasn't clicked a different card on this render yet). Drives the
  // "Suggested for you" hint — once they pick something, the hint
  // shouldn't follow them (it's a first-render orientation cue, not a
  // permanent decoration).
  const [autoPicked, setAutoPicked] = useState<boolean>(true);

  function pickPersona(scenario: DevScenario) {
    setDemoMode(true);
    setActiveScenario(scenario.name);
    setLastScenario(scenario.name);
    // Issue 024 — sessionStorage authorization flag (Option A). Brief 27 +
    // Brief 38 made the dev-shim path-gate strict: dev-shim only mounts on
    // `/demo*`. The historical post-pick contract is `assign('/')`, which
    // re-mounts AuthProvider on a non-`/demo` path, where the strict gate
    // refuses to activate dev-shim — leaving the visitor on the
    // LandingPage. The fix differentiates "this-session-deliberately-
    // authorized" (the visitor just clicked a scenario) from "stale-
    // localStorage-from-old-visit" (an issue 002 leak): we set a
    // sessionStorage flag here that the AuthProvider widens its gate on.
    // sessionStorage is tab-scoped + cleared on tab close, so a returning
    // visitor 3 days later (with stale localStorage but no flag) still
    // sees LandingPage — issue 002 closure preserved.
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem("tarii.dev_shim_session_authorized", "1");
      // Issue 030 — store the demo team_id at pick time so the
      // <DevShimSessionGuard/> in AuthContext can discriminate "still on
      // demo" from "navigated to a real team" without a backend round-trip.
      // Guard against null — un-seeded scenarios have no team to navigate
      // from anyway, and the guard only kicks in when both sides are set.
      if (scenario.team_id) {
        window.sessionStorage.setItem("tarii.dev_shim_demo_team_id", scenario.team_id);
      }
    }
    // Write the dev-shim id directly — the AuthContext's signIn() is a
    // no-op when Clerk is the active provider, but we want the demo
    // flow to override Clerk. Hard-reload (not router-navigate) so
    // `AuthProvider` re-evaluates and mounts `DevShimAuthProvider`
    // (which it does whenever a dev-shim id is present in localStorage,
    // even with Clerk configured).
    setDevAccountId(scenario.account_id);
    window.location.assign("/");
  }

  function highlightScenario(scenario: DevScenario) {
    setHighlighted(scenario.name);
    setLastScenario(scenario.name);
    setAutoPicked(false);
  }

  return (
    <div data-marketing-brand="" className="min-h-screen bg-bg">
      <main className="mx-auto flex min-h-screen max-w-4xl flex-col gap-10 px-4 py-12 sm:px-6">
        <div className="flex flex-col gap-3">
          {/* #2779 — a way back to the website. */}
          <a
            href="/"
            onClick={leaveDemoOnClick}
            className="inline-flex min-h-tap items-center gap-1.5 self-start text-sm text-fg-muted underline-offset-4 hover:text-fg hover:underline"
            data-testid="demo-scenarios-back-to-site"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to the website
          </a>
          <a
            href="/"
            onClick={leaveDemoOnClick}
            aria-label="Back to the Tarii home page"
            className="self-start"
          >
            <Logo className="h-10" />
          </a>
          <PageLead
            title="Try the Tarii demo"
            subtitle="Four preloaded scenarios. Click a card to step in as that scenario's lead. Each scenario has a different set of components switched on."
            state={
              <div className="flex flex-wrap items-center gap-2">
                <Badge>
                  <Sparkles className="h-3 w-3" aria-hidden="true" />
                  No sign-up
                </Badge>
              </div>
            }
          />
        </div>

        <section>
          {scenariosQuery.isLoading ? (
            <div className="grid gap-3">
              <Skeleton className="h-36" />
              <Skeleton className="h-36" />
            </div>
          ) : scenariosQuery.isError ? (
            <ErrorState
              title="Couldn't load demo scenarios"
              message={
                scenariosQuery.error instanceof Error
                  ? scenariosQuery.error.message
                  : "The /api/dev/scenarios endpoint is reachable in RUNTIME ∈ {dev, test, ci, demo}. If you're hitting prod, the backend may not be in demo mode."
              }
              onRetry={() => scenariosQuery.refetch()}
            />
          ) : scenariosQuery.data && scenariosQuery.data.length > 0 ? (
            <ul className="grid gap-4 sm:grid-cols-2">
              {scenariosQuery.data.map((scenario) => {
                const isHighlighted = scenario.name === highlighted;
                const showSuggestedHint = isHighlighted && autoPicked;
                const teachingSubtitle = TEACHING_SUBTITLES[scenario.name];
                return (
                  <li key={scenario.name}>
                    <Card
                      // Page-level focal: the highlighted (auto-picked or
                      // last-clicked) card carries an accent ring so the
                      // visitor's eye lands there first. Calm, not
                      // aggressive (no shadow-jump, no scale).
                      className={cn(
                        "marketing-brand-panel h-full transition-shadow",
                        isHighlighted ? "border-accent ring-1 ring-accent/30" : null,
                      )}
                      data-testid={`scenario-card-${scenario.name}`}
                      data-highlighted={isHighlighted ? "true" : "false"}
                      onClick={() => highlightScenario(scenario)}
                    >
                      <CardHeader className="flex-row items-start gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-soft text-accent-fg">
                          <Layers className="h-5 w-5" aria-hidden="true" />
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <CardTitle className="text-base">{scenario.label}</CardTitle>
                            {/* "Suggested for you" hint — calm neutral pill,
                            shown only on the auto-picked card and only
                            until the visitor clicks a different card.
                            Per F5: a first-render orientation cue, not
                            a permanent decoration or popup. */}
                            {showSuggestedHint ? (
                              <Badge
                                tone="accent"
                                data-testid={`scenario-suggested-${scenario.name}`}
                              >
                                Suggested for you
                              </Badge>
                            ) : null}
                          </div>
                          {/* "What this teaches" subtitle — the F5 fix. One
                          line, supporting context (`text-fg-muted`),
                          rendered directly under the scenario name so
                          the visitor knows what each card is *for*
                          before they click in. Older deploys without
                          a manifest entry render no subtitle (graceful
                          degrade — see TEACHING_SUBTITLES doc). */}
                          {teachingSubtitle ? (
                            <p
                              className="mt-1 text-sm text-fg-muted"
                              data-testid={`scenario-teaches-${scenario.name}`}
                            >
                              {teachingSubtitle}
                            </p>
                          ) : null}
                          <p className="mt-0.5 text-xs text-fg-subtle">{scenario.team_name}</p>
                        </div>
                        <Badge tone={scenario.loaded ? "success" : "neutral"}>
                          {scenario.loaded ? "Ready" : "First load"}
                        </Badge>
                      </CardHeader>
                      <CardContent className="flex flex-col gap-3 pb-4">
                        <p className="text-sm text-fg-muted">{scenario.description}</p>
                        {/* `story` is the paragraph-length narrative the
                        backend (commit 7fad755) ships per scenario.
                        Rendered in `text-fg-subtle` so it sits below
                        the one-line `description` in the visual
                        hierarchy. Older backend deploys may omit the
                        field; we render only when present. */}
                        {scenario.story ? (
                          <p
                            className="text-sm leading-relaxed text-fg-subtle"
                            data-testid={`scenario-story-${scenario.name}`}
                          >
                            {scenario.story}
                          </p>
                        ) : null}
                        {/* `try_this` is the 1-3 short prompts that orient
                        the demo-watcher on landing. Framed as quiet
                        suggestions ("Try opening the X tab"), not
                        gamified callouts — per brand pillars
                        (worker-friendly + trustable). */}
                        {scenario.try_this && scenario.try_this.length > 0 ? (
                          <div
                            className="flex flex-col gap-1.5"
                            data-testid={`scenario-try-this-${scenario.name}`}
                          >
                            <p className="text-xs font-medium uppercase tracking-wide text-fg-muted">
                              Try this
                            </p>
                            <ul className="flex flex-col gap-1 pl-4 text-sm text-fg-muted [list-style-type:disc] marker:text-fg-subtle">
                              {scenario.try_this.map((item, idx) => (
                                <li key={`${scenario.name}-try-${idx}`}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                        {scenario.members_summary.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {scenario.members_summary.slice(0, 4).map((m) => (
                              <Badge key={`${scenario.name}-${m.display_name}`}>
                                <Users className="h-3 w-3" aria-hidden="true" />
                                {m.display_name} · {m.role}
                              </Badge>
                            ))}
                            {scenario.members_summary.length > 4 ? (
                              <Badge>+{scenario.members_summary.length - 4} more</Badge>
                            ) : null}
                          </div>
                        ) : null}
                        <Button
                          size="sm"
                          className="self-start"
                          onClick={(e) => {
                            // Stop propagation so the card-level onClick
                            // (which only re-highlights) doesn't fire just
                            // before the page reload — keeps the audit-log
                            // click semantics clean.
                            e.stopPropagation();
                            pickPersona(scenario);
                          }}
                        >
                          Open scenario
                          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                        </Button>
                      </CardContent>
                    </Card>
                  </li>
                );
              })}
            </ul>
          ) : (
            <EmptyState
              icon={Layers}
              title="No demo scenarios loaded"
              description="The backend has no scenario JSONs registered. Run `python scripts/seed.py --all` against the demo database."
            />
          )}
        </section>
      </main>
    </div>
  );
}
